<script setup>
  import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { useFetch, useMediaQuery } from '@vueuse/core'
  import FlyoutMenuItems from '@/components/Ui/FlyoutMenuItems.vue'
  import FlyoutMobile from '@/components/Ui/FlyoutMobile.vue'
  import { useAccessibility } from '@/composables/accessibility'

  const items = ref(undefined)
  const smallItems = ref(undefined)
  const menuStructured = ref([])
  const menuReady = ref(true)

  const isLargeScreen = useMediaQuery('(min-width: 1024px)')

  const { registerEscapeKeyHandler } = useAccessibility()
  let unregisterEscapeKeyHandler

  const props = defineProps({
    open: Boolean
  })

  let previousBodyOverflow
  let previousHtmlOverflow

  const toggleScrollLock = (shouldLock) => {
    if (typeof window === 'undefined') {
      return
    }

    const body = document.body
    const html = document.documentElement

    if (!body || !html) {
      return
    }

    if (shouldLock) {
      if (previousBodyOverflow === undefined) {
        previousBodyOverflow = body.style.overflow
      }

      if (previousHtmlOverflow === undefined) {
        previousHtmlOverflow = html.style.overflow
      }

      body.style.overflow = 'hidden'
      html.style.overflow = 'hidden'
      return
    }

    if (previousBodyOverflow !== undefined) {
      body.style.overflow = previousBodyOverflow
      previousBodyOverflow = undefined
    }

    if (previousHtmlOverflow !== undefined) {
      html.style.overflow = previousHtmlOverflow
      previousHtmlOverflow = undefined
    }
  }

  watch(() => props.open, (isOpen) => {
    toggleScrollLock(isOpen)
  }, { immediate: true })

  onMounted(async () => {
    await nextTick()
    unregisterEscapeKeyHandler = registerEscapeKeyHandler(closeMenu)
    try {
      await fetchMenuItems()
      await fetchSmallMenuItems()
    } catch (error) {
      console.error(error)
    } finally {
      menuReady.value = true
    }
  })

  onBeforeUnmount(() => {
    unregisterEscapeKeyHandler?.()
    toggleScrollLock(false)
  })

  const fetchMenuItems = async () => {
    const { data, error } = await useFetch('/api/v1/menu/flyout')

    if (error.value) {
      throw new Error(error.value)
      return
    }
    items.value = JSON.parse(data.value)
  }

  const fetchSmallMenuItems = async () => {
    const { data, error } = await useFetch('/api/v1/menu/flyout-small-links')

    if (error.value) {
      throw new Error(error.value)
      return
    }

    smallItems.value = JSON.parse(data.value)
  }

  const emit = defineEmits(['close'])
  const closeMenu = () => {
    setTimeout(() => {
      emit('close')
    }, 1000)

  }
</script>

<template>
  <Transition
    name="menu-fade"
  >
  <div
    class="flyout-menu bg-primary z-[90] overflow-hidden fixed"
    v-if="open"
    :class="open ? 'open' : ''"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="flyout-menu-header pl-6 lg:pl-16 grid grid-cols-3"
    >
      <div class="menu-header-logo ~pt-4/8 lg:border-r border-white/30">
        <a
          aria-label="Logo Link zur Startseite"
          href="/"
        >
          <AppIcon
            name="LogoFullWhite"
            class="h-6 lg:h-9 w-auto"
          />
        </a>
      </div>
      <div class="col-start-3 flyout-menu-close flex justify-end w-full lg:w-auto">
        <button
          @click="closeMenu"
          class="p-4 lg:p-10 flyout-menu-close-button border opacity-80 hover:opacity-100 transition-opacity !border-white/30"
        >
          <AppIcon
            name="Close"
          />
        </button>
      </div>
    </div>
    <div
      v-if="menuReady && isLargeScreen"
      class="pl-16 h-[92vh] grid grid-cols-3"
    >
      <FlyoutMenuItems
        :open="open"
        :items="items.data"
        :smallItems="smallItems.data"
      />
    </div>
    <div
      v-if="menuReady && !isLargeScreen"
    >
      <FlyoutMobile
        :open="open"
        :items="items.data"
        :smallItems="smallItems.data"
      />
    </div>
  </div>
  </Transition>
</template>

<style lang="scss" scoped>
  .menu-fade-enter-active, .menu-fade-leave-active {
    transition: opacity 0.5s ease;
  }
  .menu-fade-enter-from, .menu-fade-leave-to {
    opacity: 0;
  }
  .flyout-menu {
    @apply pointer-events-none w-screen h-screen top-0 left-0;
    &.open {
      @apply pointer-events-auto w-screen h-screen top-0 left-0;
    }
  }
  .menu-header-logo :deep(svg) {
    @apply h-6 lg:h-9 w-auto;
  }

  .flyout-menu-close :deep(svg) {
    @apply h-4 lg:h-auto w-auto;
  }
</style>
